/**
 * Paywall SmartLawyer
 * ────────────────────
 * Logique :
 *   - Anonyme  → 3 fiches/jour  (cookie sl_uid côté backend)
 *   - Free     → 5 fiches/jour  (Firebase uid côté backend)
 *   - Paid     → illimité
 *
 * Le tracking est 100% côté backend (Qdrant collection fiche_views).
 * Le cookie sl_uid est posé par le backend (httpOnly, non manipulable).
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { usePublicAuth } from '@/hooks/usePublicAuth';

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserType = 'anon' | 'free' | 'paid';

export interface PaywallState {
  blocked:   boolean;
  remaining: number;
  userType:  UserType;
}

// ── Hook usePaywall ───────────────────────────────────────────────────────────

export function usePaywall() {
  const { getToken } = usePublicAuth();

  const [paywallState, setPaywallState] = useState<PaywallState>({
    blocked:   false,
    remaining: 3,
    userType:  'anon',
  });

  const recordView = useCallback(async (identifier: string) => {
    if (!identifier) return;
    try {
      const token = await getToken();   // null si anonyme
      const res = await fetch('/api/fiches/views/record', {
        method:      'POST',
        headers:     {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body:        JSON.stringify({ ecli: identifier }),
        credentials: 'include',   // envoyer/recevoir le cookie sl_uid
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPaywallState({
        blocked:   data.blocked   ?? false,
        remaining: data.remaining ?? 0,
        userType:  data.user_type ?? 'anon',
      });
    } catch {
      // En cas d'erreur réseau : ne pas bloquer l'accès
      setPaywallState({ blocked: false, remaining: 3, userType: 'anon' });
    }
  }, [getToken]);

  /** Applique directement le résultat _paywall retourné par by-slug */
  const applyServerState = useCallback((data: { blocked: boolean; remaining: number; user_type: string }) => {
    setPaywallState({
      blocked:   data.blocked   ?? false,
      remaining: data.remaining ?? 0,
      userType:  (data.user_type as UserType) ?? 'anon',
    });
  }, []);

  return { ...paywallState, recordView, applyServerState };
}

// ── FreeBanner ────────────────────────────────────────────────────────────────

interface FreeBannerProps {
  remaining: number;
  userType:  UserType;
}

export const FreeBanner = ({ remaining, userType }: FreeBannerProps) => {
  const { t } = useTranslation();
  // Paid = illimité, pas de banner
  if (userType === 'paid') return null;
  // Plus de fiches dispo = on laisse le PaywallOverlay prendre le relais
  if (remaining <= 0) return null;

  const isAnon = userType === 'anon';

  return (
    <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="text-amber-500">💡</span>
        <p className="text-sm text-amber-800 dark:text-amber-300">
          <Trans
            i18nKey="paywall.remaining"
            count={remaining}
            components={[<strong key="0" />]}
          />
        </p>
      </div>
      <Link
        to={isAnon ? '/login' : '/settings/subscription'}
        className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-400 underline hover:no-underline whitespace-nowrap"
      >
        {isAnon ? t('paywall.create_account_link') : t('paywall.unlimited_link')}
      </Link>
    </div>
  );
};

// ── PaywallOverlay ────────────────────────────────────────────────────────────

interface PaywallOverlayProps {
  userType: UserType;
}

export const PaywallOverlay = ({ userType }: PaywallOverlayProps) => {
  const { t } = useTranslation();
  const isAnon = userType === 'anon';

  return (
    <div className="relative -mt-24 pt-24">
      {/* Gradient de fondu */}
      <div
        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))' }}
      />

      <div className="relative z-10 rounded-2xl border-2 border-primary/20 bg-primary/5 shadow-2xl p-8 sm:p-10 text-center max-w-lg mx-auto my-10">
        <div className="text-5xl mb-5">⚖️</div>

        {isAnon ? (
          /* ── Anonyme ── */
          <>
            <h3 className="text-xl font-bold mb-2">
              {t('paywall.limit_title')}
            </h3>
            <p className="text-muted-foreground text-sm mb-2 leading-relaxed">
              <Trans
                i18nKey="paywall.anon_viewed"
                components={[<strong key="0" className="text-foreground" />]}
              />
            </p>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              <Trans
                i18nKey="paywall.anon_cta_text"
                components={[<strong key="0" className="text-foreground" />]}
              />
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/login"
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              >
                {t('paywall.create_free')}
              </Link>
              <Link
                to="/login?intent=subscribe"
                className="px-6 py-3 rounded-xl border-2 hover:bg-accent transition-colors text-sm font-medium"
              >
                {t('paywall.subscribe_from')}
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {t('paywall.free_account_note')}
            </p>
          </>
        ) : (
          /* ── Free connecté ── */
          <>
            <h3 className="text-xl font-bold mb-2">
              {t('paywall.limit_title')}
            </h3>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              <Trans
                i18nKey="paywall.free_viewed"
                components={[<strong key="0" className="text-foreground" />]}
              />
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/settings/subscription"
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              >
                {t('paywall.subscribe_from')}
              </Link>
              <Link
                to="/login"
                className="px-6 py-3 rounded-xl border-2 hover:bg-accent transition-colors text-sm font-medium"
              >
                {t('paywall.have_account')}
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {t('paywall.no_commitment')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// ── Exports legacy (pour ne pas casser les imports existants) ─────────────────

/** @deprecated Utiliser usePaywall() à la place */
export function recordView(_ecli: string): { blocked: boolean; remaining: number } {
  console.warn('recordView() est déprécié — utiliser usePaywall().recordView()');
  return { blocked: false, remaining: 3 };
}

export const MAX_FREE     = 5;
export const MAX_FREE_DAY = 3;